// Managed-contract domain types: the register entity (ManagedContract),
// its people/extension-option sub-shapes, performance grade, and
// create/update/list-response payloads. Mirrors backend managed-contract
// models. Re-exported from types/index.ts so import sites are unaffected.

export type ManagedContractStatus =
  | "draft"
  | "active"
  | "expired"
  | "terminated"
  | "archived";

export type ManagedContractType = "contract" | "arrangement";

export type DurationUnit = "days" | "months" | "years";

export interface ExtensionPeriod {
  periodNumber: number;
  exercised: boolean;
  exercisedDate?: string;
  newEndDate?: string;
}

export interface ExtensionOptions {
  durationValue: number;
  durationUnit: DurationUnit;
  noticePeriodDays?: number;
  periods: ExtensionPeriod[];
}

export interface PersonReference {
  name: string;
  email?: string;
  /** Per-person opt-in for overdue + due-soon email alerts. Undefined
   *  defaults to true at submit time. Server enforces `true` for the
   *  manager role; the form's manager input keeps the toggle locked on. */
  alertsEnabled?: boolean;
  /** Optional link to an OrganisationUser. When set on an owner/manager
   *  whose linked OrgUser has contractAccess='selected', the server
   *  auto-grants an ACL row transactionally. */
  userId?: string;
}

/** Canonical contract-people role tags. Free-text role labels are also
 *  accepted (max 60 chars) for additional alert recipients beyond the two
 *  required canonical roles. */
export type ContractPersonRole = "owner" | "manager" | string;

/** A named person on a contract. Owner + manager are required (exactly one
 *  of each). Additional rows are free-text role labels for alert recipients.
 *  `userId` optionally links to an OrganisationUser; when linked on an
 *  owner/manager whose OrgUser has contractAccess='selected', the server
 *  auto-grants an ACL row (transactional). */
export interface ContractPerson {
  role: ContractPersonRole;
  name: string;
  email?: string;
  alertsEnabled: boolean;
  userId?: string;
}

// Convenience accessors mirroring the backend `ownerOf` / `managerOf`
// helpers in backend/src/models/managedContract.ts. Tolerant of an absent
// contractPeople array — returns undefined so callers don't have to guard.
export function ownerOf(contract: {
  contractPeople?: ContractPerson[];
}): ContractPerson | undefined {
  return (contract.contractPeople ?? []).find((p) => p.role === "owner");
}
export function managerOf(contract: {
  contractPeople?: ContractPerson[];
}): ContractPerson | undefined {
  return (contract.contractPeople ?? []).find((p) => p.role === "manager");
}

export interface ExternalLink {
  label: string;
  url: string;
}

export interface ManagedContract {
  id: string;
  organisationId: string;
  type: ManagedContractType;
  // contractNumber/supplierName/startDate/endDate are required on writes
  // (the backend create schema enforces them) but OPTIONAL on reads:
  // stored rows can predate the requirement or be written outside the API
  // (e.g. seeded e2e contracts), and the read path returns items as-is.
  // Optional here forces every consumer to guard.
  contractNumber?: string;
  title: string;
  description?: string;
  supplierId: string;
  supplierName?: string;
  contractTypeId: string;
  contractTypeLabel: string;
  businessUnitId?: string;
  businessUnitLabel?: string;
  procurementMethodId?: string;
  procurementMethodLabel?: string;
  startDate?: string;
  endDate?: string;
  extensionOptions?: ExtensionOptions;
  /** Unified roster of named people on the contract — owner + manager
   *  (required, exactly one of each on writes) plus any free-text-role
   *  alert recipients. The backend mapper guarantees a populated array
   *  on every read except corrupted legacy rows; consumers should use
   *  the `ownerOf` / `managerOf` helpers from this module. */
  contractPeople?: ContractPerson[];
  status: ManagedContractStatus;
  /** Initial contract value as entered on creation (ex-GST). Persists
   *  unchanged for the life of the contract — see `currentValue` for
   *  the derived total that includes approved variations + active /
   *  completed SOWs. */
  value: number;
  /** Server-derived current total value: head value + active/completed
   *  SOWs + approved variation impacts. Surfaced on the contract
   *  register list response only; absent on detail fetches and on
   *  endpoints that do not enrich. Frontend consumers should fall
   *  back to `value` when undefined. */
  currentValue?: number;
  arrangementId?: string;
  generatedContractId?: string;
  notes?: string;
  links?: ExternalLink[];
  /** Target compliance percentage for this contract's overall KPI trend
   *  chart + summary-strip compliance rate tile. Normalised to 95 on
   *  read when absent. Integer 1–100. */
  complianceTarget?: number | null;
  /** Per-contract performance gauge thresholds. When unset, surfaces
   *  fall back to the SKILL.md defaults: success=90, watch=70.
   *  `score >= performanceSuccessThreshold` → success (emerald);
   *  `score >= performanceWatchThreshold`   → watch (amber);
   *  `score <  performanceWatchThreshold`   → at risk (red).
   *  Backend Zod refinement enforces watch < success when both supplied. */
  performanceSuccessThreshold?: number | null;
  performanceWatchThreshold?: number | null;
  /** Single-mode per-contract override for the meeting on-time grace
   *  window (days). 0 = no grace; null = use the cadence-proportional
   *  formula. Mutually exclusive with meetingGraceDaysByFrequency at
   *  the form level. */
  meetingGraceDays?: number | null;
  /** Per-frequency override map. Keys: daily / weekly / monthly /
   *  quarterly / annually / oneOff. Each value is integer days 0–60.
   *  Any unset key falls through to meetingGraceDays then the formula. */
  meetingGraceDaysByFrequency?: {
    daily?: number;
    weekly?: number;
    monthly?: number;
    quarterly?: number;
    annually?: number;
    oneOff?: number;
  } | null;
  /** Whether GST applies to dollar values on this contract. Stored as
   *  intent only — display surfaces derive ex-GST vs inc-GST by
   *  multiplying `value` by 1.1 for the inc-GST presentation. When
   *  unset (undefined), surfaces fall back to the placeholder "GST
   *  status not recorded" so the unfilled state is visible. */
  gstApplies?: boolean;
  /** Per-contract format for SOW numbers. `"nn"` allocates plain
   *  zero-padded sequentials (`01`, `02`, ...). `"sow-yyyy-nnn"`
   *  allocates year-prefixed identifiers (`SOW-2026-001`).
   *  When absent, surfaces fall back to `"nn"`. */
  sowNumberFormat?: "nn" | "sow-yyyy-nnn";
  /** Per-contract format for Variation numbers. `"nn"` for plain
   *  sequentials, `"var-yyyy-nnn"` for year-prefixed (`VAR-2026-001`).
   *  When absent, surfaces fall back to `"nn"`. */
  variationNumberFormat?: "nn" | "var-yyyy-nnn";
  /** Server-computed performance grade for the Contract Register
   *  column. Populated by GET /manage/contracts; may be absent on
   *  detail responses or older API versions. */
  performanceGrade?: PerformanceGrade;
  /** Server-computed contract-value sparkline (recent periods, ex-GST)
   *  for the Contract Register's trend column. Populated by the register
   *  full-drain path of GET /manage/contracts; absent on limited list
   *  callers and detail responses. */
  valueTrend?: number[];
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export type GradeLetterValue = "A" | "B" | "C" | "D" | "E";

export interface PerformanceGrade {
  /** null = awaiting data (zero pillars reporting). */
  grade: GradeLetterValue | null;
  /** Equal-weighted average over reporting pillars. */
  score: number | null;
  /** Locked label vocabulary: Excellent / Good / Monitor /
   *  Needs improvement / Critical / Awaiting data. */
  label: string;
  /** Populated only when the floor rule actually capped the grade. */
  capReason?: string;
  /** Number of pillars (0–3) included in the average. When < 3 the
   *  grade is partial; surfaces should annotate accordingly. */
  reportingPillarCount: number;
}

export interface ManagedContractListResponse {
  items: ManagedContract[];
  nextToken?: string;
}

/** Summary strip for the Contract Register, computed over the final
 *  enriched + filtered item set (post full-drain). totalValue is ex-GST
 *  across all statuses in view; expiring is null when no active contract
 *  ends within the next 90 days. */
export interface ContractRegisterSummary {
  contractCount: number;
  totalValue: number;
  expiring: {
    count: number;
    soonestDate: string;
    soonestTitle: string;
  } | null;
}

/** Envelope returned by GET /manage/contracts when no `limit` is passed
 *  (the Contract Register full-drain path). Distinct from the limited
 *  ManagedContractListResponse — no nextToken, carries the summary strip
 *  plus full-drain completeness metadata. */
export interface ContractRegisterResponse {
  items: ManagedContract[];
  summary: ContractRegisterSummary;
  complete: boolean;
  lastComputedAt: string;
  summaryScope: string;
}

export interface CreateManagedContractInput {
  type: ManagedContractType;
  contractNumber: string;
  title: string;
  description?: string;
  supplierId: string;
  supplierName: string;
  contractTypeId: string;
  contractTypeLabel: string;
  businessUnitId?: string;
  businessUnitLabel?: string;
  procurementMethodId?: string;
  procurementMethodLabel?: string;
  startDate: string;
  endDate: string;
  extensionOptions?: ExtensionOptions;
  /** Authoritative roster — exactly one owner + exactly one manager on
   *  writes. Free-text-role entries are additional alert recipients. */
  contractPeople?: ContractPerson[];
  status: ManagedContractStatus;
  value: number;
  arrangementId?: string;
  generatedContractId?: string;
  notes?: string;
  links?: ExternalLink[];
  complianceTarget?: number | null;
  /** Single-mode per-contract override for the meeting on-time grace
   *  window (days). 0 = no grace; null = use the cadence-proportional
   *  formula. Mutually exclusive with meetingGraceDaysByFrequency at
   *  the form level. */
  meetingGraceDays?: number | null;
  /** Per-frequency override map. Keys: daily / weekly / monthly /
   *  quarterly / annually / oneOff. Each value is integer days 0–60.
   *  Any unset key falls through to meetingGraceDays then the formula. */
  meetingGraceDaysByFrequency?: {
    daily?: number;
    weekly?: number;
    monthly?: number;
    quarterly?: number;
    annually?: number;
    oneOff?: number;
  } | null;
  gstApplies?: boolean;
  sowNumberFormat?: "nn" | "sow-yyyy-nnn";
  variationNumberFormat?: "nn" | "var-yyyy-nnn";
}

export interface UpdateManagedContractInput {
  type?: ManagedContractType;
  contractNumber?: string;
  title?: string;
  description?: string;
  supplierId?: string;
  supplierName?: string;
  contractTypeId?: string;
  contractTypeLabel?: string;
  businessUnitId?: string;
  businessUnitLabel?: string;
  procurementMethodId?: string;
  procurementMethodLabel?: string;
  startDate?: string;
  endDate?: string;
  extensionOptions?: ExtensionOptions;
  contractPeople?: ContractPerson[];
  status?: ManagedContractStatus;
  value?: number;
  arrangementId?: string;
  generatedContractId?: string;
  notes?: string;
  links?: ExternalLink[];
  complianceTarget?: number | null;
  /** Single-mode per-contract override for the meeting on-time grace
   *  window (days). 0 = no grace; null = use the cadence-proportional
   *  formula. Mutually exclusive with meetingGraceDaysByFrequency at
   *  the form level. */
  meetingGraceDays?: number | null;
  /** Per-frequency override map. Keys: daily / weekly / monthly /
   *  quarterly / annually / oneOff. Each value is integer days 0–60.
   *  Any unset key falls through to meetingGraceDays then the formula. */
  meetingGraceDaysByFrequency?: {
    daily?: number;
    weekly?: number;
    monthly?: number;
    quarterly?: number;
    annually?: number;
    oneOff?: number;
  } | null;
  gstApplies?: boolean;
  sowNumberFormat?: "nn" | "sow-yyyy-nnn";
  variationNumberFormat?: "nn" | "var-yyyy-nnn";
  version: number;
  reason?: string;
}
